import { chakra } from '@chakra-ui/react';
import gql from 'graphql-tag';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Select, { ActionMeta, StylesConfig } from 'react-select';
import { useGetCustomersByOrganizationQuery } from './__generated__/CustomerSelect.graphql';

export interface CustomerType {
  value: string;
  label: string | undefined;
}

const emptyCustomer: CustomerType = {
  value: '',
  label: '',
};

interface props {
  organizationId: string | null;
  onChange: (value: CustomerType) => void;
  tabIndex: number;
  customer?: string;
}

export function CustomerSelect({ organizationId, onChange, tabIndex }: props) {
  const { data, error, refetch } = useGetCustomersByOrganizationQuery({
    variables: {
      organizationId: organizationId ?? '',
    },
    skip: !organizationId,
  });

  const customers = data?.customersByOrganization ?? [];

  const CustomerOptions: CustomerType[] = customers.map((customer) => ({
    value: customer.id,
    label: customer.name,
  }));

  const defaultCustomer: CustomerType = {
    value: '',
    label: '',
  };
  const [selectedCustomer, setSelectedCustomer] = useState(defaultCustomer);
  useEffect(() => {
    if (organizationId) {
      refetch({ organizationId });
      setSelectedCustomer(emptyCustomer);
    }
  }, [organizationId, refetch]);

  const currentCustomer = useMemo(() => {
    if (!selectedCustomer.value) {
      return null;
    }
    return CustomerOptions.find((option) => option.value === selectedCustomer.value);
  }, [CustomerOptions, selectedCustomer.value]);

  const formatOptionLabel = useCallback((data: CustomerType) => {
    return <chakra.span>{data.label}</chakra.span>;
  }, []);

  const handleChange = async (option: CustomerType | null, meta: ActionMeta<CustomerType>) => {
    if (meta.action === 'clear') {
      setSelectedCustomer(emptyCustomer);
      onChange(emptyCustomer);
    } else {
      setSelectedCustomer(option ?? emptyCustomer);
      onChange(option ?? emptyCustomer);
    }
  };

  const customStyles: StylesConfig<CustomerType, false> = {
    placeholder: (base) => ({
      ...base,
      fontSize: '22px',
      color: '#333',
    }),
  };
  if (error) {
    return (
      <chakra.div color="red.500" fontSize="sm" p={2}>
        Failed to load customers. Please try again later.
      </chakra.div>
    );
  }
  return (
    <Select
      value={currentCustomer}
      options={CustomerOptions}
      tabIndex={tabIndex}
      onChange={handleChange}
      formatOptionLabel={formatOptionLabel}
      placeholder={'- - -'}
      isDisabled={!organizationId}
      isLoading={false}
      isMulti={false}
      escapeClearsValue={true}
      isClearable={false}
      isSearchable={true}
      menuPlacement="auto"
      styles={customStyles}
    />
  );
}

CustomerSelect.graphql = {
  queries: {
    GetCustomersByOrganization: gql`
      query GetCustomersByOrganization($organizationId: ID!) {
        customersByOrganization(organizationId: $organizationId) {
          id
          name
          handle
        }
      }
    `,
  },
};
